/**
 * Funzione PURA che normalizza la risposta del backend
 * `/opportunities/created/{id}` in uno snapshot pronto per popolare il
 * form di edit-mode.
 *
 * Estratta da `useOpportunityEditPrefill.js` il 25/02/2026 in un file
 * separato per disaccoppiare la logica pura (testabile) dall'hook React
 * (che importa axios — ESM non transformabile da Jest out-of-the-box).
 *
 * @param {object} raw - response del backend GET /opportunities/created/{id}
 * @param {object} opts
 * @param {boolean} opts.virtualDuplicate - flag URL per duplicate-to-indiretti
 * @param {string} opts.dupTarget - "clinico" | "orto" (solo se virtualDuplicate)
 * @param {boolean} opts.isReadonly - flag URL ?readonly=1 (priorità su stage-lock)
 *
 * @returns snapshot normalizzato (selectedPatient, nome, cognome, ...)
 */

import {
  REPARTI_CONFIG,
  STAGES_WHITELIST_INDIRETTI_SPLIT,
} from "@/lib/opportunity-constants";

export function normalizeBackendOpportunity(raw, { virtualDuplicate, dupTarget, isReadonly } = {}) {
  // Pre-populate patient: we don't fetch the full GHL contact, we just
  // re-create a minimal selectedPatient object using the stored fields.
  const splitName = (raw.contact_name || "").trim().split(/\s+/);
  const nome = splitName.slice(0, -1).join(" ") || "";
  const cognome = splitName.slice(-1)[0] || "";

  const selectedPatient = {
    id: raw.contact_id,
    firstName: nome,
    lastName: cognome,
    phone: raw.telefono || "",
    email: raw.email || "",
  };

  // Reverse-map pipeline_id+stage_id → reparto key.
  let reparto = Object.keys(REPARTI_CONFIG).find(
    (k) => REPARTI_CONFIG[k].pipelineId === raw.pipeline_id,
  ) || "";

  let stageId = raw.stage_id || "";

  // Virtual Duplicate override: forza la pipeline al TARGET Indiretti
  // (clinico/orto in base al bottone scelto). Sostituisce il reparto
  // appena settato dalla sorgente. Stage ID reset a "" così il backend
  // pesca il primo stage della pipeline target.
  if (virtualDuplicate && dupTarget) {
    reparto = dupTarget === "orto" ? "indiretti_ortodonzia" : "indiretti_clinico";
    stageId = "";
  }

  // Data piano cura (avoid timezone drift by parsing as local).
  let dataPianoCura = null;
  if (raw.data_piano_cura) {
    const [y, m, d] = (raw.data_piano_cura || "").split("-").map(Number);
    if (y && m && d) dataPianoCura = new Date(y, m - 1, d);
  } else if (raw.created_at && virtualDuplicate) {
    // Virtual Duplicate: se la sorgente non ha `data_piano_cura` salvato
    // esplicitamente, usa la data di CREAZIONE del piano sorgente (non
    // oggi). Coerente con la richiesta "la data deve rimanere quella
    // dell'opportunita' sorgente".
    try {
      const d = new Date(raw.created_at);
      if (!isNaN(d.getTime())) dataPianoCura = d;
    } catch {
      // parse error → resta null
    }
  }

  const piano = Array.isArray(raw.piano_cura) ? raw.piano_cura : [];
  const pianoCura = piano.map((g, idx) => ({
    id: g.id || idx + 1,
    denti: g.denti || [],
    fullMouth: !!g.fullMouth,
    trattamenti: (g.trattamenti || []).map((t, ti) => ({
      id: ti + 1,
      treatmentCode: t.treatmentCode,
      treatmentName: t.treatmentName,
      euro: t.euro,
      priceChoice: t.priceChoice || "",
      indirettoTarget: t.indirettoTarget || "",
    })),
  }));

  const editingMeta = {
    revision_count: raw.revision_count || 1,
    last_updated_at: raw.last_updated_at || null,
    last_updated_by_email: raw.last_updated_by_email || "",
    created_at: raw.created_at || null,
    medico_name: raw.medico_name || "",
    assicurazione_name: raw.assicurazione_name || "",
  };

  // Snapshot the GHL pipeline+stage at the moment of opening so we can
  // detect, on submit, whether the user changed the reparto. If they
  // didn't, we keep the original stage instead of overwriting it with
  // the reparto's default (which would always reset the opportunity to
  // the first stage of the pipeline).
  const editingPrefill = {
    medico_id: raw.medico_id || "",
    medico_name: raw.medico_name || "",
    assistente_id: raw.assistente_id || "",
    assistente_name: raw.assistente_name || "",
    assicurazione_id: raw.assicurazione_id || "",
    assicurazione_name: raw.assicurazione_name || "",
    original_pipeline_id: raw.pipeline_id || "",
    original_stage_id: raw.stage_id || "",
  };

  // Stage-lock detection: il backend espone `is_editable` (calcolato
  // su pipeline+stage live). Se false, abilitiamo la sola lettura
  // anche senza `?readonly=1` e mostriamo un banner dedicato.
  let stageLockedInfo = null;
  if (raw.is_editable === false && !isReadonly) {
    // Lo split verso Indiretti è permesso anche su stage avanzati della
    // pipeline assicurativa (whitelist: "Da autorizzare", "Caricati",
    // "Preventivi da caricare", "Preventivi caricati", "Caricato da
    // Paziente"). Richiesta utente 04/06/2026.
    const allowIndirettoSplit =
      STAGES_WHITELIST_INDIRETTI_SPLIT.has((raw.stage_name || "").trim().toLowerCase()) &&
      (raw.pipeline_id === "529iRmd3Wh7bjdgfHpP9" || // Assic. Clinico
        raw.pipeline_id === "Eu6Xz0GLvHfN5PejJ4SV"); // Assic. Ortodonzia
    stageLockedInfo = {
      stageId: raw.stage_id || "",
      stageName: raw.stage_name || "",
      allowIndirettoSplit,
    };
  }

  return {
    selectedPatient,
    nome,
    cognome,
    telefono: raw.telefono || "",
    email: raw.email || "",
    medico: raw.medico_id || "",
    assistente: raw.assistente_id || "",
    assicurazione: raw.assicurazione_id || "",
    reparto,
    stageId,
    dataPianoCura,
    pianoCura,
    existingPhotoUrls: raw.foto_urls || [],
    editingMeta,
    editingPrefill,
    stageLockedInfo,
    // Persistent user-unlock: se l'admin ha sbloccato questa opp (stato
    // salvato in DB), il flag arriva dal backend e si applica a TUTTI
    // gli utenti, USER inclusi.
    userUnlock: raw.user_unlock === true,
  };
}
